import React from 'react';
import { SalesLayout } from '@/components/SalesLayout';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
};

function GoogleSheetsSettings({ user, onLogout }) {
  const navigate = useNavigate();
  const [connected, setConnected] = React.useState(false);
  const [spreadsheetId, setSpreadsheetId] = React.useState('');
  const [syncing, setSyncing] = React.useState(false);
  const [oauthConfigured, setOauthConfigured] = React.useState(true);
  const [autoSync, setAutoSync] = React.useState(false);
  const [syncInterval, setSyncInterval] = React.useState(5);
  const [lastSyncTime, setLastSyncTime] = React.useState(null);
  const [nextSyncAt, setNextSyncAt] = React.useState(null);
  const [syncStatus, setSyncStatus] = React.useState('idle');
  const [lastError, setLastError] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    loadSettings();
  }, []);

  // Poll for auto-sync status updates (every 30 seconds to reduce load)
  React.useEffect(() => {
    let pollInterval;
    if (autoSync && connected) {
      pollInterval = setInterval(async () => {
        await loadAutoSyncStatus();
      }, 30000); // Poll every 30 seconds
    }
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [autoSync, connected]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      // Check connection status
      const statusRes = await axios.get(`${BACKEND_URL}/api/sheets/status?user_id=${user.id}`, getAuthHeader());
      setConnected(statusRes.data.connected);
      if (statusRes.data.spreadsheet_id) {
        setSpreadsheetId(statusRes.data.spreadsheet_id);
      }

      // Load auto-sync settings
      const syncRes = await axios.get(`${BACKEND_URL}/api/sheets/auto-sync?user_id=${user.id}`, getAuthHeader());
      setAutoSync(syncRes.data.enabled || false);
      setSyncInterval(syncRes.data.interval_minutes || 5);
      if (syncRes.data.spreadsheet_id) {
        setSpreadsheetId(syncRes.data.spreadsheet_id);
      }
      if (syncRes.data.last_sync_at) {
        setLastSyncTime(new Date(syncRes.data.last_sync_at).toLocaleTimeString());
      }
      if (syncRes.data.next_sync_at) {
        setNextSyncAt(new Date(syncRes.data.next_sync_at));
      }
      setSyncStatus(syncRes.data.sync_status || 'idle');
      setLastError(syncRes.data.last_error);
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
    setLoading(false);
  };

  const loadAutoSyncStatus = async () => {
    try {
      const syncRes = await axios.get(`${BACKEND_URL}/api/sheets/auto-sync?user_id=${user.id}`, getAuthHeader());
      if (syncRes.data.last_sync_at) {
        setLastSyncTime(new Date(syncRes.data.last_sync_at).toLocaleTimeString());
      }
      if (syncRes.data.next_sync_at) {
        setNextSyncAt(new Date(syncRes.data.next_sync_at));
      }
      setSyncStatus(syncRes.data.sync_status || 'idle');
      setLastError(syncRes.data.last_error);
    } catch (error) {
      console.error('Failed to load auto-sync status:', error);
    }
  };

  const handleConnect = async () => {
    try {
      window.location.href = `${BACKEND_URL}/api/oauth/sheets/login?user_id=${user.id}`;
    } catch (error) {
      if (error.response?.status === 400) {
        setOauthConfigured(false);
        toast.error('Google OAuth not configured. Please contact administrator.');
      }
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Google Sheets? This will disable auto-sync.')) {
      return;
    }
    
    try {
      await axios.post(`${BACKEND_URL}/api/oauth/sheets/disconnect?user_id=${user.id}`, null, getAuthHeader());
      setConnected(false);
      setAutoSync(false);
      setSyncStatus('idle');
      setLastError(null);
      toast.success('Google account disconnected successfully');
    } catch (error) {
      toast.error('Failed to disconnect: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSync = async () => {
    if (!spreadsheetId.trim()) {
      toast.error('Please enter a Google Spreadsheet ID');
      return;
    }

    setSyncing(true);
    try {
      await axios.post(`${BACKEND_URL}/api/sheets/sync`, null, {
        params: {
          user_id: user.id,
          spreadsheet_id: spreadsheetId
        },
        ...getAuthHeader()
      });
      
      const now = new Date().toLocaleTimeString();
      setLastSyncTime(now);
      setSyncStatus('idle');
      setLastError(null);
      toast.success('Bookings synced to Google Sheets successfully!');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message;
      // Check if it's a token expiry error
      if (error.response?.status === 401 || errorMsg.toLowerCase().includes('expired') || errorMsg.toLowerCase().includes('reconnect')) {
        setSyncStatus('token_expired');
        setLastError('Google authorization expired');
        toast.error('Google authorization expired. Please click "Reconnect Account" to re-authenticate.');
      } else {
        toast.error('Failed to sync: ' + errorMsg);
      }
    }
    setSyncing(false);
  };

  const toggleAutoSync = async (enabled) => {
    if (enabled && !spreadsheetId.trim()) {
      toast.error('Please enter Spreadsheet ID first');
      return;
    }
    
    console.log('Toggling auto-sync:', { enabled, spreadsheetId, userId: user.id, syncInterval });
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/sheets/auto-sync?user_id=${user.id}`,
        {
          enabled: enabled,
          interval_minutes: syncInterval,
          spreadsheet_id: spreadsheetId
        },
        getAuthHeader()
      );
      
      console.log('Auto-sync response:', response.data);
      setAutoSync(enabled);
      
      if (enabled) {
        toast.success(`Auto-sync enabled! Syncing every ${syncInterval} minutes`);
        if (response.data.next_sync_at) {
          setNextSyncAt(new Date(response.data.next_sync_at));
        }
      } else {
        toast.info('Auto-sync disabled');
        setNextSyncAt(null);
      }
    } catch (error) {
      console.error('Auto-sync error:', error.response?.data || error.message);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message;
      toast.error('Failed to update auto-sync: ' + (typeof errorMsg === 'object' ? JSON.stringify(errorMsg) : errorMsg));
    }
  };

  const handleIntervalChange = async (newInterval) => {
    setSyncInterval(newInterval);
    
    if (autoSync) {
      try {
        const response = await axios.post(
          `${BACKEND_URL}/api/sheets/auto-sync?user_id=${user.id}`,
          {
            enabled: true,
            interval_minutes: newInterval,
            spreadsheet_id: spreadsheetId
          },
          getAuthHeader()
        );
        toast.info(`Sync interval updated to ${newInterval} minutes`);
        if (response.data.next_sync_at) {
          setNextSyncAt(new Date(response.data.next_sync_at));
        }
      } catch (error) {
        toast.error('Failed to update interval');
      }
    }
  };

  // Calculate countdown
  const [countdown, setCountdown] = React.useState(null);
  React.useEffect(() => {
    let countdownInterval;
    if (autoSync && nextSyncAt) {
      const updateCountdown = () => {
        const now = new Date();
        const diff = nextSyncAt - now;
        if (diff > 0) {
          const minutes = Math.floor(diff / 60000);
          const seconds = Math.floor((diff % 60000) / 1000);
          setCountdown(`${minutes}m ${seconds}s`);
        } else {
          setCountdown('Syncing...');
        }
      };
      updateCountdown();
      countdownInterval = setInterval(updateCountdown, 1000);
    } else {
      setCountdown(null);
    }
    return () => {
      if (countdownInterval) clearInterval(countdownInterval);
    };
  }, [autoSync, nextSyncAt]);

  if (loading) {
    return (
      <SalesLayout user={user} onLogout={onLogout} title="Google Sheets Integration">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading settings...</div>
        </div>
      </SalesLayout>
    );
  }

  return (
    <SalesLayout user={user} onLogout={onLogout} title="Google Sheets Integration">
      <div className="max-w-2xl">
        {!oauthConfigured && (
          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mb-6">
            <h3 className="font-semibold text-yellow-800 mb-2">⚠️ Google OAuth Not Configured</h3>
            <p className="text-sm text-yellow-700 mb-3">
              The Google Sheets integration requires OAuth credentials to be configured in the backend.
            </p>
            <div className="text-sm text-yellow-700 space-y-2">
              <p className="font-medium">Administrator Setup Required:</p>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>Go to <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console</a></li>
                <li>Create OAuth 2.0 credentials and add redirect URI</li>
                <li>Add credentials to backend .env file</li>
                <li>Restart the backend server</li>
              </ol>
            </div>
          </div>
        )}

        <div className="bg-white p-6 rounded-lg border mb-6">
          <h2 className="text-2xl font-bold mb-4">Google Sheets Integration</h2>
          <p className="text-gray-600 mb-6">
            Connect your Google account to sync bookings data to Google Sheets automatically.
          </p>

          {/* Connection Status */}
          <div className="mb-6 p-4 rounded-lg bg-gray-50">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Connection Status</p>
                <p className={`text-sm mt-1 ${connected ? 'text-green-600' : 'text-gray-600'}`}>
                  {connected ? '✓ Connected to Google Sheets' : '○ Not Connected'}
                </p>
                {/* Show token expired warning */}
                {(syncStatus === 'token_expired' || (lastError && lastError.includes('expired'))) && (
                  <p className="text-sm mt-1 text-red-600">
                    ⚠️ Google authorization expired. Please reconnect your account.
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                {!connected && oauthConfigured && (
                  <button
                    onClick={handleConnect}
                    className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    data-testid="connect-google-btn"
                  >
                    Connect Google Account
                  </button>
                )}
                {connected && (
                  <>
                    {/* Show Reconnect button if token expired */}
                    {(syncStatus === 'token_expired' || (lastError && lastError.includes('expired'))) && (
                      <button
                        onClick={handleConnect}
                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                        data-testid="reconnect-google-btn"
                      >
                        🔄 Reconnect Account
                      </button>
                    )}
                    <button
                      onClick={handleDisconnect}
                      className="px-4 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200"
                      data-testid="disconnect-google-btn"
                    >
                      Disconnect
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
          {!connected && !oauthConfigured && (
            <div className="mb-6 p-4 rounded-lg bg-gray-50">
              <button
                disabled
                className="px-6 py-2 bg-gray-400 text-white rounded cursor-not-allowed"
                title="OAuth not configured"
              >
                Connection Unavailable
              </button>
            </div>
          )}

          {connected && (
            <div className="space-y-6">
              {/* Spreadsheet ID */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Google Spreadsheet ID *
                </label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  value={spreadsheetId}
                  onChange={(e) => setSpreadsheetId(e.target.value)}
                  placeholder="1mZWwGxqWI7yV4ef-G1A_AzhmzF2MMBtqp6lTpaK3NyY"
                  data-testid="spreadsheet-id-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Paste only the ID from your Google Sheets URL (between /d/ and /edit)
                </p>
              </div>

              {/* Auto-Sync Toggle */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="font-medium text-blue-900">Auto-Sync Mode</p>
                    <p className="text-sm text-blue-700 mt-1">
                      Automatically sync bookings at regular intervals (server-side)
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={autoSync}
                      onChange={(e) => toggleAutoSync(e.target.checked)}
                      disabled={!spreadsheetId.trim()}
                      data-testid="auto-sync-toggle"
                    />
                    <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                {/* Sync Interval Selector */}
                {autoSync && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-blue-900 mb-2">
                      Sync Interval
                    </label>
                    <select
                      className="w-full p-2 border border-blue-300 rounded bg-white"
                      value={syncInterval}
                      onChange={(e) => handleIntervalChange(parseInt(e.target.value))}
                    >
                      <option value={1}>Every 1 minute</option>
                      <option value={5}>Every 5 minutes</option>
                      <option value={10}>Every 10 minutes</option>
                      <option value={15}>Every 15 minutes</option>
                      <option value={30}>Every 30 minutes</option>
                      <option value={60}>Every 1 hour</option>
                    </select>
                  </div>
                )}

                {/* Sync Status */}
                {autoSync && (
                  <div className="mt-4 p-3 bg-white border border-blue-200 rounded">
                    <div className="flex items-center justify-between text-sm">
                      <div>
                        <p className="text-gray-600">
                          <span className="font-medium">Status:</span>{' '}
                          {syncStatus === 'running' ? (
                            <span className="text-blue-600">● Syncing...</span>
                          ) : syncStatus === 'error' ? (
                            <span className="text-red-600">● Error</span>
                          ) : (
                            <span className="text-green-600">● Active</span>
                          )}
                        </p>
                        {lastSyncTime && (
                          <p className="text-gray-600 mt-1">
                            <span className="font-medium">Last sync:</span> {lastSyncTime}
                          </p>
                        )}
                        {lastError && (
                          <p className="text-red-500 text-xs mt-1">
                            Error: {lastError}
                          </p>
                        )}
                      </div>
                      {countdown && (
                        <div className="text-right">
                          <p className="text-gray-500 text-xs">Next sync in</p>
                          <p className="text-blue-600 font-mono font-bold">{countdown}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Manual Sync Button */}
              <div className="flex gap-3">
                <button
                  onClick={handleSync}
                  disabled={syncing || !spreadsheetId.trim()}
                  className="flex-1 px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="sync-now-btn"
                >
                  {syncing ? 'Syncing...' : 'Sync Now'}
                </button>
                {lastSyncTime && !autoSync && (
                  <div className="flex items-center text-sm text-gray-600">
                    Last: {lastSyncTime}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
          <h3 className="font-semibold mb-3">Setup Instructions:</h3>
          <ol className="list-decimal list-inside space-y-2 text-sm">
            <li>Click "Connect Google Account" and authorize access</li>
            <li>Create a Google Sheet with a tab named "Data"</li>
            <li>Copy only the Spreadsheet ID from URL (not the full URL)</li>
            <li>Paste the ID above</li>
            <li>Enable Auto-Sync or click "Sync Now"</li>
          </ol>
          <div className="mt-4 p-3 bg-white border border-blue-200 rounded">
            <p className="text-xs text-gray-700 mb-2"><strong>What gets synced?</strong></p>
            <p className="text-xs text-gray-600">
              All booking records with key fields: Booking No, Customer Name, Mobile, Email, Vehicle Brand/Model, 
              Registration Number, Insurance Details, RTO Area, and Booking Date.
            </p>
          </div>
        </div>
      </div>
    </SalesLayout>
  );
}

export default GoogleSheetsSettings;
